/**
 * The service calendar — the grid behind **Service Scheduling** (design §12).
 *
 * The allocation LIST answers "what is the state of every service". This answers
 * the question a list cannot: **is Thursday full**. Scheduling is a shape
 * problem — four jobs on one day and none on the next is invisible in a table
 * sorted by date, and obvious the moment the week is drawn.
 *
 * DRAGGING IS THE POINT. A read-only calendar would be a picture of the problem;
 * dropping a card runs the same audited `allocate(date, driver)` the list's own
 * date and driver cells run, so a move made by dragging produces the same
 * history as one typed into a row. Dropping on the Unscheduled tray clears the
 * date, which is how a service comes back off the calendar.
 *
 * FOUR VIEWS, offered through `views` so a host can take a subset:
 *   Day       working hours down the side
 *   Week      seven columns, plus the Unscheduled tray
 *   Month     the whole month
 *   Timeline  one row per DRIVER — the only view where a single drag answers
 *             "when" and "who" together, because its two axes are those fields
 *
 * It is a component rather than part of the page because the calendar is a
 * self-contained interaction: a second copy of it would be two drag-and-drop
 * surfaces to keep writing the same audit rows.
 */
import { useMemo, useState } from 'react';
import { fmtLocalDate } from '../../utils/datetime';
import { allocationStatus, type ServiceAllocationRow, type ServiceCategoryDef } from './clientRequestApi';

export type CalView = 'day' | 'week' | 'month' | 'timeline';

/** The working day the Day view lays out. Outside these the board is empty,
 *  and a 24-row grid of empty hours is harder to read than the eight that
 *  matter. A service scheduled outside them still shows — in the OFF-HOURS
 *  row at the end, so nothing can be scheduled into a slot the screen hides. */
const HOURS = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
               '13:00', '14:00', '15:00', '16:00', '17:00'];

/** yyyy-MM-dd for a local date — the key both the wire and the cells use.
 *  Built from local parts, never toISOString(), which would shift the day
 *  backwards for anyone east of UTC and silently file work under the wrong
 *  date (§14b.6 — no inline date maths that can move a day). */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
       + `-${String(d.getDate()).padStart(2, '0')}`;
}

/** The Sunday that starts the week containing `d`. */
function weekStart(d: Date): Date {
  const s = new Date(d);
  s.setDate(s.getDate() - s.getDay());
  s.setHours(0, 0, 0, 0);
  return s;
}

/** Two letters for the driver avatar — the timeline's row label has to stay
 *  narrow enough that seven day columns still fit. */
function initials(name: string): string {
  const first = name.split(' \u00b7 ')[0] ?? name;
  return first.split(/\s+/).filter(Boolean)
    .map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

interface Props {
  rows: ServiceAllocationRow[];
  defs: ServiceCategoryDef[];
  /** Drivers — the Timeline's rows. */
  techs: { id: string; label: string }[];
  /** Move a service to a day, or clear its date when `date` is null. The SAME
   *  call the list's date cell makes, so both views write one path. */
  onMove: (row: ServiceAllocationRow, date: string | null) => void | Promise<void>;
  /** Set the date AND the driver in one move — what a Timeline drop means, since
   *  its two axes ARE those two fields. Falls back to `onMove` when absent. */
  onAssign?: (row: ServiceAllocationRow, date: string | null, driverId: string | null)
    => void | Promise<void>;
  /** Which views this host offers. The allocation board wants a quick second
   *  read of its own rows (week/month); the Scheduling screen is the dedicated
   *  calendar and offers all four. */
  views?: CalView[];
  /** Lets the host label itself for the current view — Timeline is doing
   *  something different enough from a calendar to need saying. */
  onViewChange?: (v: CalView) => void;
  /** CONTROLLED mode. Pass this and the host owns the current view — and with
   *  it the switcher, which the calendar then stops rendering in its own nav
   *  bar. That is how Service Scheduling puts Day/Week/Month/Timeline up on the
   *  page title row (prototype v68) without two switchers fighting over one
   *  piece of state. Omit it and the calendar keeps both, as the allocation
   *  board does. */
  view?: CalView;
  busyId: string | null;
}

/** The Day/Week/Month/Timeline switcher, exported so a host that takes control
 *  of `view` renders the SAME control rather than a look-alike — one set of
 *  labels, one set of styles, one place to change them. */
export function CalViewSwitch(
  { views, view, onPick }:
  { views: CalView[]; view: CalView; onPick: (v: CalView) => void },
) {
  return (
    <div className="sc-views">
      {views.map(v => (
        <button key={v} className={view === v ? 'on' : ''} onClick={() => onPick(v)}>
          {v === 'day' ? 'Day' : v === 'week' ? 'Week'
            : v === 'month' ? 'Month' : 'Timeline'}
        </button>
      ))}
    </div>
  );
}

export default function ServiceAllocationCalendar({
  rows, defs, techs, onMove, onAssign, views = ['week', 'month'], onViewChange, busyId,
  view: viewProp,
}: Props) {
  const controlled = viewProp !== undefined;
  const [ownView, setOwnView] = useState<CalView>(views[0] ?? 'week');
  const view = controlled ? viewProp! : ownView;
  const [anchor, setAnchor] = useState(() => new Date());
  const [dragId, setDragId] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  /** Which day's "Open day" button holds focus. State, not `:focus` in CSS:
   *  the button is hidden until revealed, and a keyboard user must be able
   *  to SEE what they have focused. A state class always paints; the
   *  pseudo-class proved unreliable here even with !important. */
  const [kbDay, setKbDay] = useState<string | null>(null);

  const today = dayKey(new Date());

  const catOf = (code?: string | null) => defs.find(d => d.code === code);

  /** Services grouped by their day, so a cell is a lookup rather than a filter
   *  over every row for every month cell. */
  const byDay = useMemo(() => {
    const m = new Map<string, ServiceAllocationRow[]>();
    for (const r of rows) {
      if (!r.scheduledDate) continue;
      const k = r.scheduledDate.slice(0, 10);
      const list = m.get(k);
      if (list) list.push(r); else m.set(k, [r]);
    }
    return m;
  }, [rows]);

  const unscheduled = useMemo(() => rows.filter(r => !r.scheduledDate), [rows]);

  /** The seven days of the anchor's week. */
  const week = useMemo(() => {
    const s = weekStart(anchor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(s); d.setDate(s.getDate() + i); return d;
    });
  }, [anchor]);

  /** FIVE rows, which is what almost every month needs and what the screen is
   *  laid out for. Six only when the month genuinely spills into a sixth week —
   *  a 31-day month starting on a Friday or Saturday — because the alternative
   *  is a calendar that silently hides the last days of the month. Never four:
   *  a February starting on a Sunday would otherwise shrink the grid and make
   *  the page jump. */
  const month = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const s = weekStart(first);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    // Rounded, not floored: across a DST change the two dates are 31.96 or
    // 32.04 days apart, and flooring loses a week once a year.
    const span = Math.round((last.getTime() - s.getTime()) / 86_400_000) + 1;
    const weeks = Math.max(5, Math.ceil(span / 7));
    return Array.from({ length: weeks * 7 }, (_, i) => {
      const d = new Date(s); d.setDate(s.getDate() + i); return d;
    });
  }, [anchor]);

  const shift = (n: number) => {
    const d = new Date(anchor);
    if (view === 'day') d.setDate(d.getDate() + n);
    else if (view === 'month') d.setMonth(d.getMonth() + n, 1);
    else d.setDate(d.getDate() + n * 7);        // week and timeline both step a week
    setAnchor(d);
  };

  const pick = (v: CalView) => { if (!controlled) setOwnView(v); onViewChange?.(v); };

  /* Dates in the canonical MM/DD/YYYY, through the SHARED helper — the label
     used to be built inline with long-form month names ("Thu 10 September
     2026"), which is the one thing §18.6 says never to do with a date. A month
     header stays a month NAME: it names a month rather than a date, and is the
     calendar-navigation case §18.6 exempts. */
  const label =
      view === 'day'   ? `${DOW[anchor.getDay()]} ${fmtLocalDate(dayKey(anchor))}`
    : view === 'month' ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
    : `${fmtLocalDate(dayKey(week[0]!))} – ${fmtLocalDate(dayKey(week[6]!))}`;

  const drop = (key: string | null) => {
    const row = rows.find(r => r.id === dragId);
    setDragId(null); setOverKey(null);
    // Dropping a service back on the day it already sits on is not a change,
    // and writing one would put a no-op in the audit trail.
    if (!row || (row.scheduledDate ?? '').slice(0, 10) === (key ?? '')) return;
    void onMove(row, key);
  };

  const card = (r: ServiceAllocationRow) => {
    const c = catOf(r.categoryCode);
    const s = allocationStatus(r);
    const time = (r.scheduledTime ?? '').slice(0, 5);
    return (
      <div key={r.id}
           className={`sc-card ${c?.tone ?? 'neutral'}${busyId === r.id ? ' busy' : ''}`}
           draggable={busyId !== r.id}
           onDragStart={() => setDragId(r.id)}
           onDragEnd={() => { setDragId(null); setOverKey(null); }}
           title={`${r.serviceNo ?? ''} · ${c?.name ?? ''}\n${r.clientName ?? ''}\n`
                + `${r.siteAddress ?? 'No address'}\n${s.label}`}>
        {/* Category, not the service number, is the card's headline — what a
            dispatcher scanning a week is sorting by (prototype v68's schedCard()).
            The service number is still one hover away, in the tooltip. */}
        <div className="sc-card-t">
          {c?.icon && <i className={`ti ${c.icon}`} aria-hidden="true" />}
          {c?.name ?? r.serviceNo ?? '—'}
        </div>
        <div className="sc-card-s">{r.clientName ?? '—'}</div>
        {/* Time, then who is carrying it — what dispatch scans for after the
            category colour, matching prototype v68's card face. */}
        <div className="sc-card-w">
          {time && <span className="sc-card-time">{time}</span>}
          {r.assignedToName
            ? r.assignedToName.split(' · ')[0]
            : <span className="na">Unassigned</span>}
        </div>
      </div>
    );
  };

  /** Month ▸ an empty day is BOOKABLE, not blank: open it in Day view, where
   *  the hours are, and schedule into one. Offered only where it can be acted
   *  on — an in-month day with nothing on it, and only if Day is a view this
   *  calendar has. A day that already holds work is a drop target instead. */
  const openDay = (d: Date) => {
    setAnchor(new Date(d));
    pick('day');            // pick, not setView — the host is told too
  };

  /** MONTH's card is a one-line CHIP — category icon + client (v68's
   *  `schedCard(x, true)` / `.chip2`). The three-line card belongs in Day and
   *  Week, where a cell is tall; in a 112px month square it fits barely two,
   *  so a busy day reads as "full" when it holds three. */
  const chip = (r: ServiceAllocationRow) => {
    const c = catOf(r.categoryCode);
    const s = allocationStatus(r);
    return (
      <div key={r.id}
           className={`sc-chip ${c?.tone ?? 'neutral'}${busyId === r.id ? ' busy' : ''}`}
           draggable={busyId !== r.id}
           onDragStart={e => { e.stopPropagation(); setDragId(r.id); }}
           onDragEnd={() => { setDragId(null); setOverKey(null); }}
           title={`${r.serviceNo ?? ''} · ${c?.name ?? ''}
${r.clientName ?? ''}
`
                + `${r.siteAddress ?? 'No address'}
${s.label}`}>
        {c?.icon && <i className={`ti ${c.icon}`} />}
        <span>{r.clientName ?? '—'}</span>
      </div>
    );
  };

  const cell = (d: Date, outside: boolean) => {
    const key  = dayKey(d);
    const mine = byDay.get(key) ?? [];
    const wknd = d.getDay() === 0 || d.getDay() === 6;
    const free = view === 'month' && !outside && mine.length === 0
              && views.includes('day');
    return (
      <div key={key + (outside ? '-o' : '')}
           className={`sc-day${wknd ? ' wknd' : ''}${key === today ? ' today' : ''}`
                    + `${outside ? ' out' : ''}${overKey === key ? ' over' : ''}`
                    + `${free ? ' free' : ''}`}
           onClick={free ? () => openDay(d) : undefined}
           title={free ? `Open ${fmtLocalDate(key)} to schedule` : undefined}
           onDragOver={e => { e.preventDefault(); setOverKey(key); }}
           onDragLeave={() => setOverKey(k => (k === key ? null : k))}
           onDrop={e => { e.preventDefault(); drop(key); }}>
        <div className="sc-day-h">
          <span className="sc-day-n">{d.getDate()}</span>
          {view === 'week' && <span className="sc-day-d">{DOW[d.getDay()]}</span>}
          {mine.length > 0 && <span className="sc-day-c">{mine.length}</span>}
        </div>
        <div className="sc-day-b">
          {/* Month caps at THREE and counts the rest. Four chips overflow a
              112px square, and a cell that silently clips its last service is
              worse than one that says how many it is not showing. */}
          {view === 'month' ? mine.slice(0, 3).map(chip) : mine.map(card)}
          {view === 'month' && mine.length > 3 && (
            <div className="sc-day-more">+{mine.length - 3} more</div>
          )}
          {/* Only in WEEK. A month is 35-odd cells, and 33 of them saying
              "Drop here" buries the two with work in them — v68's month cells
              are plain. A week is seven, where the prompt still reads as a
              prompt. */}
          {mine.length === 0 && view === 'week' && (
            <div className="sc-day-e">Drop here</div>
          )}
          {/* A real button, not a decorated div: the whole cell takes the mouse
              click, but a keyboard user needs something focusable — and CSS
              reveals the label on :focus-within as well as :hover, so it is
              never a control you can focus but not see. */}
          {free && (
            <button type="button"
                    className={`sc-day-open${kbDay === key ? ' kb' : ''}`}
                    onFocus={() => setKbDay(key)}
                    onBlur={() => setKbDay(k => (k === key ? null : k))}
                    onClick={e => { e.stopPropagation(); openDay(d); }}>
              <i className="ti ti-plus" />Open day
            </button>
          )}
        </div>
      </div>
    );
  };

  /** A Timeline drop sets BOTH axes at once: the column is the date, the row is
   *  the driver. That is the whole reason the view exists — the one place where
   *  moving a card answers "when" and "who" together. */
  const dropOnLane = (dateKey: string, driverId: string | null) => {
    const row = rows.find(r => r.id === dragId);
    setDragId(null); setOverKey(null);
    if (!row) return;
    const sameDay    = (row.scheduledDate ?? '').slice(0, 10) === dateKey;
    const sameDriver = (row.assignedToId ?? null) === driverId;
    if (sameDay && sameDriver) return;          // a no-op must not be audited
    void (onAssign ? onAssign(row, dateKey, driverId) : onMove(row, dateKey));
  };

  /**
   * Day — the working hours down the side, 07:00 to 17:00 (prototype v68).
   *
   * <p>There is no standing "Any time" row: with every service timed it was an
   * empty lane at the top of every day. But the API does NOT require a time, so
   * one CAN exist — and a view that silently hides a booking is worse than one
   * that admits it. So the untimed and the out-of-hours lanes are rendered ONLY
   * when they have something in them. On an ordinary day the grid is exactly
   * the eleven hours; on an odd one it grows a row that says what it is.</p>
   *
   * <p>Each row holds its whole hour: a visit at 10:30 sits in the 10:00 row
   * (the card shows 10:30). The Time field takes any time of day since
   * 2026-10-30, not only times on the hour.</p>
   */
  const dayGrid = () => {
    const key  = dayKey(anchor);
    const mine = byDay.get(key) ?? [];
    const hourOf = (r: ServiceAllocationRow) => {
      const t = (r.scheduledTime ?? '').slice(0, 5);
      return t ? `${t.slice(0, 2)}:00` : '';
    };
    const at   = (h: string) => mine.filter(r => hourOf(r) === h);
    const anytime = mine.filter(r => !r.scheduledTime);
    const offhours = mine.filter(r => {
      const h = hourOf(r);
      return h && !HOURS.includes(h);
    });
    const lane = (labelText: string, list: ServiceAllocationRow[], dropKey: string) => (
      <div key={labelText}
           className={`sc-hrow${overKey === dropKey ? ' over' : ''}`}
           onDragOver={e => { e.preventDefault(); setOverKey(dropKey); }}
           onDragLeave={() => setOverKey(k => (k === dropKey ? null : k))}
           onDrop={e => { e.preventDefault(); drop(key); }}>
        <div className="sc-htime">{labelText}</div>
        <div className="sc-hcell">
          {list.map(card)}
          {/* No "Drop here" per hour: the day has thirteen rows, and thirteen
              identical labels is noise that hides the two rows with work in it.
              The empty row is its own invitation, and the bar already says the
              grid is a drop target. */}
        </div>
      </div>
    );
    return (
      <div className="sc-dayv">
        {HOURS.map(h => lane(h, at(h), `${key}|${h}`))}
        {anytime.length  > 0 && lane('No time set',   anytime,  `${key}|any`)}
        {offhours.length > 0 && lane('Outside hours', offhours, `${key}|off`)}
      </div>
    );
  };

  /** Timeline — one row per driver across the week, plus an Unassigned lane so
   *  work with nobody on it is visible in the same picture rather than only in
   *  the tray. Dropping into a lane assigns the driver AND the date. */
  const timelineGrid = () => {
    const weekKeys = week.map(dayKey);

    /*
     * ONE ROW PER DRIVER WHO IS WORKING THIS WEEK — plus Unassigned, always.
     *
     * The lane list used to be the whole technician lookup, which on this
     * database is every `dbo.Employee` with a display name: 74 rows, 65 of them
     * codes like "C16294" or bare "E", none with any work, and Unassigned
     * stranded at row 75. A view whose premise is "one row per driver" does not
     * survive that — the week is invisible under a wall of empty lanes.
     *
     * So a lane earns its place by having a service in the VISIBLE week. Paging
     * to another week rebuilds the list for that week, which is the behaviour a
     * dispatcher expects: the board shows who is out, not who exists.
     *
     * Unassigned is unconditional and sorted LAST — it is the tray of this view,
     * the row work is dragged OFF, and it must be there even on a week where
     * everything already has a driver.
     */
    const workingIds = new Set<string>();
    for (const k of weekKeys) {
      for (const r of byDay.get(k) ?? []) {
        if (r.assignedToId) workingIds.add(r.assignedToId);
      }
    }
    const lanes: { id: string | null; label: string }[] = [
      ...techs.filter(t => workingIds.has(t.id)).map(t => ({ id: t.id as string | null, label: t.label })),
      // A driver on the week's work whom the lookup does not know (left the lab,
      // or a roster that has moved on) still gets a lane: the service is really
      // theirs, and hiding it would lose work from the board entirely.
      ...[...workingIds]
        .filter(id => !techs.some(t => t.id === id))
        .map(id => ({
          id: id as string | null,
          label: (byDay.get(weekKeys.find(k =>
            (byDay.get(k) ?? []).some(r => r.assignedToId === id)) ?? '') ?? [])
            .find(r => r.assignedToId === id)?.assignedToName ?? 'Unknown driver',
        })),
      { id: null, label: 'Unassigned' },
    ];

    const forLane = (driverId: string | null, k: string) =>
      (byDay.get(k) ?? []).filter(r => (r.assignedToId ?? null) === driverId);

    return (
      <div className="sc-tl">
        <div className="sc-tl-head">
          <div className="sc-tl-drv">Driver</div>
          {week.map(d => (
            <div key={dayKey(d)} className={dayKey(d) === today ? 'today' : undefined}>
              {DOW[d.getDay()]} {d.getDate()}
            </div>
          ))}
        </div>
        <div className="sc-tl-body">
          {lanes.map(l => {
            const count = weekKeys.reduce((n, k) => n + forLane(l.id, k).length, 0);
            return (
              <div className="sc-tl-row" key={l.id ?? '__none'}>
                <div className="sc-tl-drv">
                  <span className="sc-av">{initials(l.label)}</span>
                  <div>
                    <div className="sc-tl-n">{l.label.split(' \u00b7 ')[0]}</div>
                    <div className="sc-tl-c">{count} this week</div>
                  </div>
                </div>
                {weekKeys.map(k => {
                  const dropKey = `${l.id ?? 'none'}|${k}`;
                  const list = forLane(l.id, k);
                  return (
                    <div key={dropKey}
                         className={`sc-tl-cell${overKey === dropKey ? ' over' : ''}`}
                         onDragOver={e => { e.preventDefault(); setOverKey(dropKey); }}
                         onDragLeave={() => setOverKey(x => (x === dropKey ? null : x))}
                         onDrop={e => { e.preventDefault(); dropOnLane(k, l.id); }}>
                      {list.map(card)}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * Where the tray goes.
   *
   * <p>Day and Week leave room for a column beside the grid; Month needs all
   * seven of its own and Timeline needs the width for a week of lanes, so those
   * two take the tray underneath instead (prototype v68). Either way it is
   * PRESENT — it used to render only in Week, which left the other three views
   * with no way to drag work onto the calendar or drop it back off.</p>
   */
  const sideTray = view === 'day' || view === 'week';

  /* The tray is where unscheduled work waits, and it is a drop target in its own
     right — dragging a card back onto it clears the date, which is the only way
     to take something off the calendar without opening the list. */
  const tray = (
    <div className={`sc-tray${overKey === '__none' ? ' over' : ''}`}
         onDragOver={e => { e.preventDefault(); setOverKey('__none'); }}
         onDragLeave={() => setOverKey(k => (k === '__none' ? null : k))}
         onDrop={e => { e.preventDefault(); drop(null); }}>
      <div className="sc-tray-h">
        <div className="sc-tray-t">Unscheduled</div>
        <div className="sc-tray-s">
          {unscheduled.length ? 'Drag into the calendar' : 'Nothing waiting'}
        </div>
      </div>
      <div className="sc-tray-b">
        {unscheduled.map(card)}
        {unscheduled.length === 0 && (
          <div className="sc-day-e">Drop here to unschedule</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="sc-wrap">
      <div className="sc-bar">
        <button className="ca-btn" onClick={() => shift(-1)} title="Previous">
          <i className="ti ti-chevron-left" />
        </button>
        <span className="sc-label">{label}</span>
        <button className="ca-btn" onClick={() => shift(1)} title="Next">
          <i className="ti ti-chevron-right" />
        </button>
        <button className="ca-btn" onClick={() => setAnchor(new Date())}>Today</button>

        <span className="sc-spacer" />

        {/* Only when the host has NOT taken the switcher (see the `view` prop). */}
        {!controlled && <CalViewSwitch views={views} view={view} onPick={pick} />}
        {unscheduled.length > 0 && (
          <span className="cr-chip warn">{unscheduled.length} unscheduled</span>
        )}
      </div>

      {/* Which colour is which category. ABOVE the grid, under the bar: the
          cards are colour-coded, so the key is only useful before the eye
          reaches them — below a month grid it is off-screen when it is needed. */}
      <div className="sc-legend">
        {defs.map(d => (
          <span key={d.code}>
            {/* A solid dot, not `.cr-chip`: the chip's background is a pale
                TINT meant to sit behind text, and at 10px with no text on it the
                colour it is keying was barely visible. */}
            <i className={`sc-key ${d.tone}`} />{d.name}
          </span>
        ))}
        <span className="sc-spacer" />
        <span className="sc-legend-note">
          {view === 'timeline'
            ? 'Drag across to change the date, or between rows to reassign the driver. '
              + 'Every move is audited.'
            : 'Drag a service to another day to reschedule it. Every move is audited.'}
        </span>
      </div>

      <div className={`sc-body${sideTray ? '' : ' wide'}`}>
        {sideTray && tray}

        {view === 'day'       ? dayGrid()
         : view === 'timeline' ? timelineGrid()
         : (
          <div className={view === 'week' ? 'sc-week' : 'sc-month'}>
            {view === 'month' && (
              <div className="sc-month-h">
                {DOW.map(d => <div key={d}>{d}</div>)}
              </div>
            )}
            <div className={view === 'week' ? 'sc-week-g' : 'sc-month-g'}>
              {view === 'week'
                ? week.map(d => cell(d, false))
                : month.map(d => cell(d, d.getMonth() !== anchor.getMonth()))}
            </div>
          </div>
        )}
      </div>

      {/* Month and Timeline use the full width, so the tray goes UNDERNEATH
          rather than stealing a column from a grid that needs all seven — and
          ONLY when something is actually waiting (v68). An empty tray saying
          "Nothing waiting" is not worth 200px of a month grid; in Day and Week
          it costs a narrow column instead, so there it always shows. */}
      {!sideTray && unscheduled.length > 0 && (
        <div className="sc-tray-under">{tray}</div>
      )}
    </div>
  );
}
